/**
 * CsvImportModal
 *
 * Three-stage modal for CSV import: Upload → Preview → Progress/Result.
 * Renders an always-open antd `<Modal>`; callers gate mount/unmount with
 * `{open && <CsvImportModal … />}` so subscriptions and parse state only run
 * while the modal is shown.
 */

import { FC, useState, useCallback, useContext } from 'react';
import { Modal, Upload, Table, Progress, Typography, Alert, Space, Button } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { MessageContext } from '@/providers/MessageContext';
import { dateOnlyDayjs } from '@/dates';
import { useArTableCsvImport } from './useArTableCsvImport';
import type { ImportResult } from './useArTableCsvImport';
import type { CsvFlatColumn } from './flattenColumnsForCsv';
import type { ArCell } from '#types/System.Artifacts.types';
import type { UIArTableColumnOverride } from '#types/System.types';

const { Text } = Typography;
const { Dragger } = Upload;

type CsvImportModalProps = {
	onCancel: () => void;
	columnConfig?: UIArTableColumnOverride[];
};

type Stage = 'upload' | 'preview' | 'importing' | 'done';

const ERROR_COLUMNS = [
	{ title: 'Row', dataIndex: 'row', key: 'row', width: 70 },
	{ title: 'Column', dataIndex: 'column', key: 'column', width: 150 },
	{ title: 'Message', dataIndex: 'message', key: 'message' },
];

const PREVIEW_DATE_FORMAT = 'YYYY-MM-DD HH:mm';
const PREVIEW_DAY_FORMAT = 'YYYY-MM-DD';

/**
 * A `date` column holds a calendar day anchored in `defaultTimeZone`, so the
 * preview reads it back through `dateOnlyDayjs`; every other column holds an
 * instant and reads browser-local, the wall clock the row will show.
 */
function formatPreviewDate(date: Date, dataType: CsvFlatColumn['dataType']): string {
	return dataType === 'date'
		? dateOnlyDayjs(date.getTime()).format(PREVIEW_DAY_FORMAT)
		: dayjs(date).format(PREVIEW_DATE_FORMAT);
}

function formatPreviewValue(value: unknown, dataType: CsvFlatColumn['dataType']): string {
	if (value === null || value === undefined) return '';
	if (Array.isArray(value)) return value.map((entry) => formatPreviewValue(entry, dataType)).join(', ');
	if (value instanceof Date) return formatPreviewDate(value, dataType);
	if (typeof value === 'object' && 'valueType' in value && value.valueType === 'manual') {
		return (value as { value?: string }).value ?? '';
	}
	if (typeof value === 'object' && 'referenceType' in value) {
		return (value as { cacheLabel?: string }).cacheLabel ?? '';
	}
	if (
		typeof value === 'object' &&
		typeof (value as { toDate?: unknown }).toDate === 'function'
	) {
		return formatPreviewDate((value as { toDate: () => Date }).toDate(), dataType);
	}
	if (typeof value === 'object' && 'fields' in value) return '(compound)';
	if (typeof value === 'object') return JSON.stringify(value);
	return String(value);
}

function getNestedValue(value: unknown, path: string): unknown {
	return path.split('.').reduce<unknown>((current, key) => {
		if (!current || typeof current !== 'object' || !(key in current)) {
			return undefined;
		}
		return (current as Record<string, unknown>)[key];
	}, value);
}

const CsvImportModal: FC<CsvImportModalProps> = ({ onCancel, columnConfig }) => {
	const [stage, setStage] = useState<Stage>('upload');
	const [importResult, setImportResult] = useState<ImportResult | null>(null);

	const { messageApi } = useContext(MessageContext);
	const { flatColumns, parseResult, resetParseResult, parseCsv, importRows, importing, progress } = useArTableCsvImport({ columnConfig });

	const reset = useCallback(() => {
		setStage('upload');
		resetParseResult();
		setImportResult(null);
	}, [resetParseResult]);

	const handleClose = useCallback(() => {
		reset();
		onCancel();
	}, [reset, onCancel]);

	const handleFileRead = useCallback((text: string) => {
		const result = parseCsv(text);

		if (result.rows.length === 0 && result.warnings.length > 0) {
			messageApi.warning(result.warnings[0]);
			return;
		}

		setStage('preview');
	}, [parseCsv, messageApi]);

	const handleUpload = useCallback((file: File) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			const text = e.target?.result;
			if (typeof text === 'string') {
				handleFileRead(text);
			}
		};
		reader.readAsText(file);
		return false;
	}, [handleFileRead]);

	const handleImport = useCallback(async () => {
		setStage('importing');
		const result = await importRows();
		setImportResult(result);
		setStage('done');
	}, [importRows]);

	const importableColumns = flatColumns.filter((c) => c.importable);

	const previewColumns = importableColumns.slice(0, 6).map((col) => ({
		title: col.header,
		dataIndex: col.typeId,
		key: `${col.typeId}${col.subFieldPath ?? ''}`,
		width: 140,
		ellipsis: true,
		render: (_: unknown, record: Record<string, unknown>) => {
			const cellKey = col.targetTypeId ?? col.typeId;
			const cell = record[cellKey] as ArCell | undefined;
			if (!cell) return <Text />;
			const previewValue = col.subFieldPath
				? getNestedValue(cell.value, col.subFieldPath)
				: cell.value;
			return <Text>{formatPreviewValue(previewValue, col.dataType)}</Text>;
		},
	}));

	const previewRows = (parseResult?.rows ?? []).slice(0, 5).map((row, idx) => ({
		key: String(idx),
		...row,
	} as Record<string, unknown>));

	const validRowCount = parseResult?.rows.length ?? 0;
	const errorCount = parseResult?.errors.length ?? 0;

	return (
		<Modal
			title="Import Data (CSV)"
			open
			onCancel={handleClose}
			width={720}
			footer={
				stage === 'preview' ? (
					<Space>
						<Button aria-label="import-csv-back" onClick={reset}>Back</Button>
						<Button
							type="primary"
							onClick={handleImport}
							disabled={validRowCount === 0}
							aria-label="import-csv-confirm"
						>
							Import {validRowCount} Row{validRowCount !== 1 ? 's' : ''}
						</Button>
					</Space>
				) : stage === 'done' ? (
					<Button type="primary" aria-label="import-csv-close" onClick={handleClose}>Close</Button>
				) : null
			}
			closable={!importing}
			mask={{ closable: !importing }}
			keyboard={!importing}
		>
			{stage === 'upload' && (
				<Dragger
					accept=".csv"
					showUploadList={false}
					beforeUpload={handleUpload}
					aria-label="csv-upload-dragger"
				>
					<p className="ant-upload-drag-icon">
						<InboxOutlined />
					</p>
					<p className="ant-upload-text">Click or drag a CSV file to upload</p>
					<p className="ant-upload-hint">
						Use the &quot;Download Template&quot; option to get a pre-formatted CSV with the correct headers.
					</p>
				</Dragger>
			)}

			{stage === 'preview' && parseResult && (
				<Space orientation="vertical" style={{ width: '100%' }} size="middle">
					<Text>
						{validRowCount} row{validRowCount !== 1 ? 's' : ''} parsed
						{errorCount > 0 && <Text type="warning"> ({errorCount} cell error{errorCount !== 1 ? 's' : ''})</Text>}
					</Text>

					{parseResult.warnings.map((w, i) => (
						<Alert key={i} type="warning" title={w} showIcon />
					))}

					{errorCount > 0 && (
						<Table
							dataSource={parseResult.errors.slice(0, 50)}
							columns={ERROR_COLUMNS}
							size="small"
							pagination={false}
							scroll={{ y: 150 }}
							rowKey={(_, idx) => `err-${idx}`}
						/>
					)}

					{previewRows.length > 0 && (
						<>
							<Text type="secondary">Preview (first 5 rows, first 6 columns)</Text>
							<Table
								dataSource={previewRows}
								columns={previewColumns}
								size="small"
								pagination={false}
								scroll={{ x: 'max-content' }}
							/>
						</>
					)}
				</Space>
			)}

			{stage === 'importing' && (
				<Space orientation="vertical" style={{ width: '100%' }} size="middle">
					<Text>Importing rows...</Text>
					<Progress
						percent={progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}
						format={() => `${progress.current} / ${progress.total}`}
					/>
				</Space>
			)}

			{stage === 'done' && importResult && (
				<Space orientation="vertical" style={{ width: '100%' }} size="middle">
					<Alert
						type={importResult.addedCount > 0 ? 'success' : 'warning'}
						title={`${importResult.addedCount} row${importResult.addedCount !== 1 ? 's' : ''} imported`}
						description={importResult.errorCount > 0
							? `${importResult.errorCount} error${importResult.errorCount !== 1 ? 's' : ''} encountered`
							: undefined
						}
						showIcon
					/>
					{importResult.errors.length > 0 && (
						<Table
							dataSource={importResult.errors.slice(0, 50)}
							columns={ERROR_COLUMNS}
							size="small"
							pagination={false}
							scroll={{ y: 200 }}
							rowKey={(_, idx) => `err-${idx}`}
						/>
					)}
				</Space>
			)}
		</Modal>
	);
};

export { CsvImportModal };
